import { useMediaQuery } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoaderContainer from '../../../../shared/components/loader/LoaderContainer';
import useKeyboardOpen from '../../../../shared/hooks/use-keyboard-open';
import SurveyAnswer from '../../../../shared/models/survey/SurveyAnswer';
import SurveyQuestion from '../../../../shared/models/survey/SurveyQuestion';
import SurveyQuestionType from '../../../../shared/models/survey/SurveyQuestionType';
import Events from '../../../../shared/utils/events';
import useResultContext from '../../../dashboard/store/UseResultContext';
import { Result } from '../../../dashboard/types';
import { postAnswers } from '../../data/SurveyService';
import useSurvey from '../../hooks/useSurvey';
import useSurveyContext from '../../store/UseSurveyContext';
import SurveyNavigation from '../surveyNavigation/SurveyNavigation';
import SurveyProgress from '../surveyProgress/SurveyProgress';
import SurveyQuestionRenderer from './SurveyQuestionRenderer';

const SurveyQuestions = () => {
  const {
    questionAnswers,
    questionNumber,
    question,
    setQuestion,
    setBackupQuestionAnswers,
    updateQuestionAnswers,
    updateSurvey,
    updateSessionKey,
    updateMetadata,
    activationCode,
    sessionKey,
    onHandleNext,
    onHandlePrevious,
  } = useSurveyContext();
  const { t } = useTranslation();
  const isKeyboardOpen = useKeyboardOpen();
  const navigator = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const isSmallDevice = useMediaQuery('only screen and (max-width: 998px)');
  const [language, setLanguage] = useState(() => localStorage.getItem('i18nextLng') ?? 'nl');

  const updateLanguage = () => {
    const newLanguage = localStorage.getItem('i18nextLng');
    if (newLanguage === null || newLanguage === 'null') {
      setLanguage('nl');
    } else {
      setLanguage(newLanguage);
    }
  };

  const { isLoading, isFetching, data } = useSurvey(activationCode, language);
  const { updateInitialResult } = useResultContext();

  useEffect(() => {
    if (data?.specification) {
      const { specification: survey, sessionKey: sKey, metadata } = data;
      const { questions } = survey;
      setQuestion(questions[questionNumber]);
      updateSessionKey(sKey);
      updateSurvey(survey);
      updateMetadata(metadata);
    }
  }, [questionNumber, data]);

  useEffect(() => {
    window.addEventListener(Events.LOCAL_STORAGE_UPDATE_LANGUAGE, () => updateLanguage());
    return () => {
      window.removeEventListener(Events.LOCAL_STORAGE_UPDATE_LANGUAGE, () => updateLanguage());
    };
  }, []);

  if (isLoading && (isFetching || !question)) {
    return <LoaderContainer text={t('survey.loading')} />;
  }

  const { specification: survey } = data!;

  const allQuestions: SurveyQuestion[] = survey.questions;

  const checkDisabledButton = () => {
    if (loading) return true;
    if (question?.questionType === SurveyQuestionType.CHAPTER_INTRODUCTION) return false;

    if (question?.allowSkipWithDefaultValue) return false;

    const surveyAnswer = questionAnswers.find((answer) => answer.questionId === question?.questionId);
    if (surveyAnswer === undefined) return true;

    const { value } = surveyAnswer;

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return false;
  };

  const handleFinished = () => {
    const userEmail = localStorage.getItem('gezondleven-email') || undefined;
    localStorage.removeItem('gezondleven-email');
    setLoading(true);
    setResultLoading(true);
    postAnswers(questionAnswers, activationCode, true, userEmail, sessionKey)
      .then((result: any) => {
        const { data: resultData } = result;
        const resultAnswers = SurveyAnswer.fromJsonList(resultData.answers);

        localStorage.removeItem('gezondleven-locked-categories');
        updateQuestionAnswers(resultAnswers);
        setBackupQuestionAnswers(resultAnswers);
        setTimeout(() => {
          navigator('/dashboard');
          setResultLoading(false);
          updateInitialResult(resultData as Result);
        }, 2000);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNext = () => {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    if (question.questionType === SurveyQuestionType.CHAPTER_INTRODUCTION) {
      onHandleNext();
      return;
    }

    onHandleNext(handleFinished);
  };

  const handlePrevious = () => {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    onHandlePrevious();
  };

  if (resultLoading) {
    return (
      <div className="question-container loading-container">
        <LoaderContainer text={t('survey.loading-results')} />
      </div>
    );
  }

  const { questionType } = question ?? {};
  const isIntroduction =
    questionType === SurveyQuestionType.INTRODUCTION || questionType === SurveyQuestionType.CHAPTER_INTRODUCTION;

  const renderProgress = !(isSmallDevice && isIntroduction);

  const totalQuestionNumber = allQuestions.filter(
    ({ questionType: surveyQuestionType }) =>
      surveyQuestionType !== SurveyQuestionType.CHAPTER_INTRODUCTION &&
      surveyQuestionType !== SurveyQuestionType.INTRODUCTION
  ).length;

  return (
    <>
      {renderProgress && (
        <div className="part progress">
          <SurveyProgress
            currentQuestion={questionNumber}
            totalQuestions={totalQuestionNumber}
          />
        </div>
      )}
      <div className={`part question ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
        <div className={`question-container ${isIntroduction ? 'introduction' : ''}`}>
          {question && <SurveyQuestionRenderer question={question} />}
          <SurveyNavigation
            onHandlePrevious={handlePrevious}
            onHandleNext={handleNext}
            onHandleFinish={handleFinished}
            firstQuestion={questionNumber === 0}
            lastQuestion={questionNumber === allQuestions.length - 1}
            nextDisabled={checkDisabledButton()}
          />
        </div>
      </div>
    </>
  );
};

export default SurveyQuestions;
